import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";
import { hasAnalyticsConsent, onConsentChange } from "@/lib/consent";
import { getAttributionPayload } from "@/lib/analytics";

/**
 * Sends exactly one GA4 page_view per route, for the initial load and for
 * every client-side navigation. The inline loader configures GA4 with
 * `send_page_view:false`, so there is no competing automatic page_view.
 */
export function GA4PageViews() {
  const location = useRouterState({ select: (s) => s.location.href });
  const lastSent = useRef<string | null>(null);
  const previous = useRef<string | null>(null);

  useEffect(() => {
    // Build the URL from router state, not window.location: on a client-side
    // navigation the effect can run before the history entry is committed.
    const url = new URL(location, window.location.origin);
    const params = url.searchParams;
    const attribution = getAttributionPayload();
    const campaign = (key: "utm_source" | "utm_medium" | "utm_campaign" | "utm_term" | "utm_content") =>
      params.get(key) ?? attribution[key];

    const send = () => {
      if (!hasAnalyticsConsent()) return;
      if (typeof window.gtag !== "function") return;
      if (lastSent.current === location) return;
      lastSent.current = location;
      window.gtag("event", "page_view", {
        page_location: url.href,
        page_title: document.title,
        page_referrer: previous.current ?? document.referrer ?? undefined,
        page_locale: url.pathname.startsWith("/es") ? "es" : "en",
        campaign_source: campaign("utm_source"),
        campaign_medium: campaign("utm_medium"),
        campaign_name: campaign("utm_campaign"),
        campaign_term: campaign("utm_term"),
        campaign_content: campaign("utm_content"),
      });
      previous.current = url.href;
    };

    // The document title is set by the route head after render, and gtag may
    // still be loading (or consent may arrive later).
    const first = window.setTimeout(send, 100);
    const timer = window.setInterval(send, 500);
    const stop = window.setTimeout(() => window.clearInterval(timer), 10000);
    const off = onConsentChange(send);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(timer);
      window.clearTimeout(stop);
      off();
    };
  }, [location]);


  return null;
}

export default GA4PageViews;
